import $ from 'jquery';
import { partials } from 'handlebars';
import App from '../app';
import Map from '../models/Map';
import MapView from '../views/MapView';
import Events from '../events';
import { SERVICE_TYPES } from '../models/Constants';
import { getClosestService, getViewpoint } from '../map/vg/vg-utils';
import { lang, getIMD, getFloorsMap, getStores, getStoreById, getStoreBySpaceCode, storeDataForAll } from '../data-utils';
import serviceMapping from '../map/vg/service-mapping';

const logError = e => console.error(e);

import lazyLoadScript from 'lazyload-script';
import VGMiddleware from '../map/vg/VGMiddleware';

import VGFacilitiesProvider from '~/service/facilities/visioglobe';
import action from '~/action';
import { typeToServiceType } from '~/service/facilities/facility-types';

export default function VGMapController (context) {

  let componentVisible = false;
  let navigationIndex = 0;
  let accessibleRouteEnabled = false;

  const {
    playTimeout: PLAY_TIMEOUT,
    replayTimeout: REPLAY_TIMEOUT
  } = context.imdSettings.feature.locations;

  const startNavigation = () => ++navigationIndex;
  const stopNavigation = startNavigation;
  const isNavigationOn = index => index == navigationIndex;

  const vgsdk = lazyLoadScript('/lib/vg.mapviewer.web-1.8.12.js', {
    id: 'vg-sdk',
    async: true
  })
    .catch(e => console.error(e));

  const onObjectMouseUp = (event, element) => {
    const poi = typeof element.options == 'function' ? element.options() : null;
    const store = getStoreBySpaceCode(element.vg.id || poi.id);
    if (store) {
      const view = $('.mapContainer').position() || { top: 0, left: 0 };
      const { offsetX, offsetY } = event.srcEvent || event;
      const maxX = $('#locationMap').width();
      const maxY = $('#locationMap').height() - $('.services').height();
      Map.showStoreMenuByStoreId(store.id, view, offsetX, offsetY, maxX, maxY);
      Events.dispatchEvent('poi', { name: store.name, action: 'popup' });
      refresh();
    }
    else if (poi) {
      const selector = `#${poi.id}`;
      if ($(selector).is('.vg__poi')) {
        vgMiddleware.then(middleware => {
          middleware.popSingleService(poi.id);
          requestAnimationFrame(() => $(`#${poi.id}`).click());
        }).catch(logError);
      }
      else {
        console.log(`User tap '${poi.id}' but it probably doesn't have service category`);
      }
    }
    else {
      console.warn(`Missing store for spaceId ${element.vg.id}`);
    }
  };

  const vgMiddleware = Promise.all([
    vgsdk,
    Events.once('app:start')
  ])
    .then(() => {
      const [ vgContainer ] = getContainer();
      const middleware = new VGMiddleware(getOptions(), vgContainer);
      bindOnce(middleware);
      Events.once('app:close')
        .then(_ => middleware.unload());
      return middleware.lock.onceReady;
    })
    .catch(e => console.error(`Failed VGMiddleware init`, e));

  const serviceTypes = vgMiddleware
    .then(middleware => {
      const { mapviewer } = middleware.get();
      context.service.facilities.setProvider(
        new VGFacilitiesProvider(mapviewer, context.imdHash)
      );
      return middleware.lock.onceServicesReady;
    })
    .then(({ serviceTypes, serviceLocations }) => {
      // These are used to highlighting service POIs. Each one gets their own.
      $(partials['vg.poi']({ serviceTypes: serviceTypes }))
        .appendTo('.vg__pois');

      storeDataForAll('servicelocations', serviceLocations);

      return serviceTypes;
    }).catch(logError);
  
  const getOptions = () => {

    const { devMode, imdId, imdHash, imdSettings, resources } = context;
    const { direction } = getIMD(imdId);
    const combinedVgOptions = Object.assign(
      {},
      resources.themeConfig.vg,
      imdSettings.integration.vg
    );

    const { floorMapping, rotationMod = 0 } = combinedVgOptions;
    const floorsMap = getFloorsMap();
    const stores = getStores().reduce(
      (acc, {spaceCode, name}) => (acc[spaceCode] = { name }, acc), {}
    );

    // Make floor mapping both ways and include floor data
    combinedVgOptions.floorMapping = Object.entries(floorMapping).reduce(
      (acc, [vgID, floorId]) => {
        const value = {
          vgID, floorId,
          floor: floorsMap[floorId]
        };
        acc[vgID] = value;
        acc[floorId] = value;
        return acc;
      }, {}
    );

    // Setup service mapping
    const serviceTypes = SERVICE_TYPES
      .map(x => (x.pinicon = resources.get(x.pinicon), x))
      .reduce(
        (acc, x) => (acc[x.id] = x, acc), {}
      );
    combinedVgOptions.serviceMapping = Object.entries(serviceMapping).reduce(
      (acc, [identity, serviceId]) => {
        const value = Object.assign({ pois: [], identity }, serviceTypes[serviceId]);
        acc[identity] = value;
        acc[serviceId] = value;
        return acc;
      }, {}
      // {
      //   'svcx.smart-posti': {
      //     pois: [], identity: 1000,
      //     id: 1000, name: 'service.smart-posti', icon: 'ic_smart-posti', pinicon: 'pin_smart-posti'
      //   }, '1000': {
      //     pois: [], identity: 1000,
      //     id: 1000, name: 'service.smart-posti', icon: 'ic_smart-posti', pinicon: 'pin_smart-posti'
      //   }
      // }
    );

    return Object.assign(
      combinedVgOptions,
      {
        imdHash,
        rotation: direction + rotationMod,
        devMode,
        imagePath: `/${imdHash}/client/vg`,
        onObjectMouseUp,
        stores
      }
    );
  };

  const getContainer = () => $(
    partials['vg.container']({})
  ).insertBefore('#view').find('.vg__map');

  const enableMap = (enable = true) => {
    $('body').toggleClass('view-maps--vg', enable);
  };

  const hightlightYouAreHere = () => rerunAnim(
    'anim-pulse__ring',
    document.querySelector('#you-are-here')
  );

  const getFacilityActions = (xs => async () => {
    if (!xs.length) {
      const provider = await context.service.facilities.getProvider();
      const types = new Set();
      xs = provider.searchItems.reduce((acc, item) => {
        const { type, vg } = item.ref;
        const { url } = vg.options();

        if (types.has(type) || !url) return acc;
        types.add(type);

        return [
          ...acc,
          {
            type,
            service: typeToServiceType(type),
            url: url
          }
        ];
      }, []);
    }
    return xs;
  })([]);

  /** @type {(animClass: string, parent: Element | Document) => void} */
  const rerunAnim = (animClass, parent = document) => {
    parent.querySelectorAll(animClass).forEach(x => {
      x.classList.remove(animClass);
      // @ts-ignore
      void x.offsetWidth;
      x.classList.add(animClass);
    });
  };

  const resize = (middleware) => {
    const width = $('.mapContainer').width();
    const height = $('.mapContainer').height();
    if (width && height) {
      middleware.resize(width, height);
    }
    return middleware;
  };

  const syncAccessibleRoute = middleware => {
    middleware.getState().accessibleRoute = accessibleRouteEnabled;
  };

  const start = () => {
    componentVisible = true;
    const params = Map.init(context, refresh);
    enableMap();

    Promise.all([vgMiddleware, serviceTypes, getFacilityActions()])
      .then(([middleware, serviceTypes, facility_actions]) => {
        syncAccessibleRoute(middleware);
        params.serviceTypes = serviceTypes;
        params.facility_actions = facility_actions;
        params.accessibleRoute = accessibleRouteEnabled;
        MapView.render(params);
        bindEvents(params, middleware);
        return middleware;
      })
      .then(resize)
      .then(middleware => {
        middleware.start();
        hightlightYouAreHere();

        //Hide close button if there is no store selected/no text in search field
        // $('.search-box__reset').toggle(!!params.poi);

        const { store, showRoute, facility } = params.poi || {};
        if (store) {
          const { spaceCode, name } = store;
          if (showRoute) {
            return middleware.showRouteTo(spaceCode, lang())
              .then(startRouteGuiding('start'))
              .catch(e => console.error(`Failed start routing to ${name}:${spaceCode}`, e));
          }
          return middleware.selectPlace(spaceCode)
            .then(() => {
              updateFloor(null, middleware, true).then(() => {
                const viewpoint = getViewpoint(
                  spaceCode,
                  middleware.get().mapviewer
                );
                const { left, top } = middleware
                  .get()
                  .mapviewer.convertPointToScreen(viewpoint);
                const view = $('.mapContainer').position();
                const maxX = $('#locationMap').width();
                const maxY = $('#locationMap').height() - $('.services').height();
                Map.showStoreMenuByStoreId(store.id, view, left, top, maxX, maxY);
                Events.dispatchEvent('poi', { name: store.name, action: 'location' });
                return refresh();
              });
            })
            .catch(e => console.error(`Failed highlight store ${name}:${spaceCode}`, e));
        }
        else if (facility) {
          try {
            middleware.selectPOI(facility);
          } catch (e) {
            console.error(`Failed highlight facility ${facility}`, e);
          }
        }
        else {
          // return middleware.changeFloor(params.floorId);
          return middleware.setupInitialPosition()
            .catch(e => console.error(`Failed initial position setup`, e));
        }
      })
      .catch(logError);
  };

  const exit = () => vgMiddleware.then(middleware => {
    middleware.stop();
    middleware.unPopServices();
    componentVisible = false;
    stopNavigation();
    enableMap(false);
  }).catch(logError);

  /** @type {(extra: object | undefined) => Promise} */
  const refresh = (extra) => {
    App.resetTimers();

    if(window.location.hash != '#maps') {
      return Promise.resolve();
    }

    const params = Map.getMapData();
    Object.assign(
      params,
      extra
    );
    params.accessibleRoute = accessibleRouteEnabled;
    MapView.render(params);

    return vgMiddleware.then(middleware => {
      resize(middleware);
      bindEvents(params, middleware);
    }).catch(logError);
  };

  const startRouteGuiding = (caller, timeout = PLAY_TIMEOUT) => navigationHelper => {

    if (!navigationHelper) {
      console.error(`Missing 'navigationHelper' for '${caller}'`);
      return Promise.resolve();
    }

    const pNavigation = startNavigation();
    const delay = t => new Promise(resolve => setTimeout(resolve, t));
    const log = msg => console.log(msg);
    const escapeOnCancel = () =>
      !isNavigationOn(pNavigation) ?
        Promise.reject(`Navigation ${pNavigation} cancelled`) :
        Promise.resolve();

    const showNext = () => {
      return escapeOnCancel()
        .then(() => Promise.all([                     // update parallel
          navigationHelper.displayNextInstruction(), // route animation &
          showNavigationGuides(navigationHelper),   // instructions
          log(` └ nav update ${pNavigation} step ${navigationHelper.currentIndex()}`)
        ]))
        .then(escapeOnCancel)
        .then(_ => {
          if (navigationHelper.instructionsLeft() && componentVisible) {
            return delay(timeout).then(showNext);
          }
          else {
            return Promise.resolve();
          }
        });
    };

    // Show instructions of the first step
    log(`Navigation ${pNavigation} started step ${navigationHelper.currentIndex()} by '${caller}'`);
    showNavigationGuides(navigationHelper);
    return delay(timeout) // wait
      .then(showNext)  // All rest steps
      .then(_ =>
        $('#replayroute').css('opacity', 1).show()
      )  // Done
      .catch(e => log(e));
  };

  const restartRouteGuiding = () => startRouteGuiding('replay', REPLAY_TIMEOUT);

  const showNavigationGuides = navigationHelper => {
    refresh(navigationHelper.getRouteData());
  };

  const updateFloorByRouting = (middleware, skipRefresh = false) => {
    let floor;
    const { state, mapviewer } = middleware.get();
    if (state.navigationHelper) {
      floor = state.navigationHelper.getCurrentRoute().floor;
    }
    else {
      floor = mapviewer.getCurrentFloor();
    }
    
    updateFloor(floor, middleware, skipRefresh);
  };

  const updateFloor = async (floor_vg, middleware, skipRefresh) => {
    if (!floor_vg) {
      floor_vg = middleware.get().mapviewer.getCurrentFloor();
    }
    const { floorId } = middleware.mapFloor(floor_vg);
    const mapdata = Map.getMapData();
    mapdata.floorId = floorId;
    mapdata.floorIndex = Map.getFloorIndex(floorId);
    return !skipRefresh && await refresh();
  };

  const clearRouting = (middleware, doReset = true) => {
    stopNavigation();
    $('#replayroute').hide();
    const params = Map.getMapData();
    params.poi = null;
    doReset && middleware.resetActivePlace();
    middleware.clearRouting();
    clearMapdata();
    middleware.unPopServices();
    return middleware;
  };

  const bindEvents = (params, middleware) => {

    //show service swipe icon only if neccessary
    $('#swipeIcon').hide();
    var servicesWidth = $('.services__container').width();
    var servicesContainerWidth = $('.services').width();
    if(servicesWidth > servicesContainerWidth){
      $('#swipeIcon').show();
    }

    $('#routesummary').hide();
    $('#zoom').hide();
    $('#pan').hide();

    $('#storemenu .info').click(({ currentTarget }) => {
      clearRouting(middleware, false);
      Map.goToStore(currentTarget.id);
      const store = getStoreById(currentTarget.id);
      const currentStore = { store, showRoute: false };
      App.saveLocally(currentStore, 'currentStore', 'all');
    });

    $('#storemenu .route').click(({ currentTarget }) => {
      const store = getStoreById(currentTarget.id);
      if (store) {
        clearRouting(middleware, false);
        syncAccessibleRoute(middleware);
        const { spaceCode } = store;
        Events.dispatchEvent('poi', { name: store.name, action: 'route' });
        middleware.showRouteTo(spaceCode, lang())
          .then(startRouteGuiding('storemenu'))
          .catch(logError);
      }
      clearMapdata();
      refresh();
    });

    $('.service').click(({ currentTarget }) => {
      const activeServiceType = params.activeServiceType;
      Map.saveState();
      clearRouting(middleware);
      toggleService(activeServiceType, currentTarget.id)
        .then(refresh)
        .catch(logError);
    });

    $('#servicemenu .location, #servicemenuModal .location').click(({ currentTarget }) => {
      clearRouting(middleware);
      refresh();
      middleware.showClosestService(currentTarget.id)
        .then(({ id }) => {
          updateFloorByRouting(middleware);
          middleware.popSingleService(id);
        })
        .catch(logError);
    });

    $('#servicemenu .route, #servicemenuModal .route').click(({ currentTarget }) => {
      clearRouting(middleware);
      syncAccessibleRoute(middleware);
      middleware.showClosestService(currentTarget.id, { route: true, lang: lang() })
        .then(_ => middleware.getState().navigationHelper)
        .then(startRouteGuiding('servicemenu'))
        .catch(logError);
      refresh();
    });

    $('#serviceLocationMenu .route').click(({ currentTarget }) => {
      clearRouting(middleware);
      syncAccessibleRoute(middleware);
      const poi = $(currentTarget).attr('poi');
      middleware.showRouteTo(poi, lang())
        .then(startRouteGuiding('serviceLocationMenu'))
        .catch(logError);
      refresh();
    });

    $('.sidebar .route-summary__accessible-toggle-input').change(({ currentTarget }) => {
      accessibleRouteEnabled = !!currentTarget.checked;
      syncAccessibleRoute(middleware);

      stopNavigation();
      middleware.reroute(lang())
        .then(navigationHelper => {
          if (navigationHelper) {
            return startRouteGuiding('accessible-route-toggle')(navigationHelper);
          }
          return refresh();
        })
        .catch(logError);
    });

    $('.sidebar').on('click', '.list-floor-element', (event) => {
      const { id } = event.currentTarget;
      if (id != params.floorId) {
        Map.setFloor(id);
        refresh();
        middleware.unPopServices();
        middleware.changeFloor(id);
        hightlightYouAreHere();
      }
    });

    $('#backbutton').on('click', () => {
      clearRouting(middleware);
      Map.goBack();
      refresh();
    });

    $('#homebutton').on('mouseup touchend', () => {
      clearRouting(middleware);
    });

    $('.zoom-controls').click(() => {
      $('#zoom').show();
      $('#pan').hide();
    });
    $('.pan-controls').click(() => {
      $('#pan').show();
      $('#zoom').hide();
    });

    const zoom = scale => () => middleware.zoom(scale);

    $('.zoom-icons')
      .on('click', '#zoom-in', zoom(.5))
      .on('click', '#zoom-out', zoom(1.75));

    const PAN_UNIT = 50;
    const pan = opt => () => middleware.pan(opt);

    $('.pan-controls')
      .on('click', '#pan-top', pan({ v: PAN_UNIT }))
      .on('click', '#pan-down', pan({ v: -PAN_UNIT }))
      .on('click', '#pan-left', pan({ h: -PAN_UNIT }))
      .on('click', '#pan-right', pan({ h: PAN_UNIT }));

    $('.next-phase').click(({ currentTarget }) => {
      middleware.goToInstruction(currentTarget.id)
        .then(startRouteGuiding(`next-phase ${currentTarget.id}`))
        .catch(logError);
    });

    $('.sidebar .route-summary__close').click(() => {
      updateFloorByRouting(middleware, true);
      clearRouting(middleware);
      refresh();
    });

    $('#replayroute').click(() => {
      stopNavigation();
      middleware.goToInstruction(0)
        .then(restartRouteGuiding())
        .catch(logError);
    });

    $('.overlay').click(function(e){
      e.stopPropagation();
      middleware.resetActivePlace();
      clearMapdata();
      refresh();
    });

    $('.search-box__input, .search-box__submit').click(() =>
      action.searchMapFeatures()
    );

    // $('.search-box__reset').click(() =>
    //   action.searchMapFeatures(true)
    // );
  };

  Events.addEventListener('session:end', () => {
    vgMiddleware.then(middleware => clearRouting(middleware))
      .catch(logError);
  });

  const bindOnce = (middleware) => {

    const $vgMap = $('.vg__map');
    const locationMenuConstants = {
      pos: { left: -22, top: -16 }, // math is hard but these seem to work
      maxX: $vgMap.width(),
      maxY: $vgMap.height()
    };

    $vgMap.on('click', '.vg__poi', ({currentTarget}) => {
      const { servicePOIs } = middleware.get();
      const service = servicePOIs[currentTarget.id];

      // TODO could check that there's route between src and dest

      const style = window.getComputedStyle(currentTarget);
      const { m11: scale, m41: x, m42: y } = new DOMMatrix(style.transform);
      const iconWidth = 16 * scale; // size comes from vg.poi.handlebars

      Map.showServiceLocationMenu(
        currentTarget.id,
        service.id,
        locationMenuConstants.pos, x, y,
        locationMenuConstants.maxX,
        locationMenuConstants.maxY,
        iconWidth
      );
      refresh();
    });
  };

  const clearMapdata = () => {
    const mapdata = Map.getMapData();
    mapdata.dots = [];
    mapdata.storemenu = {};
    mapdata.servicemenu = {};
    mapdata.serviceLocationMenu = {};
    mapdata.closestService = {};
    mapdata.closestStore = [];
    mapdata.routes = [];
    mapdata.showGuiding = false;
    mapdata.routeNotFound = false;
    mapdata.store = {};
    mapdata.service = {};
    mapdata.activeServiceType = {};
    mapdata.activeStoreSpace = '';
    mapdata.svgZoom = 0;
    mapdata.svgPan = {};

    mapdata.currentRoute = {};
    mapdata.playRoute = {};
    mapdata.playRoute.play = false;
    mapdata.playRoute.finished = false;
    mapdata.playRoute.currentRoute = {};
  };

  function toggleService(activeServiceType, serviceId) {

    return vgMiddleware.then(middleware => {
      const vgContext = middleware.get();
      const { serviceMapping, floorMapping } = vgContext.options;
      const serviceType = serviceMapping[serviceId];
      const mapdata = Map.getMapData();
      const { imdFloorId, floorId: currentFloorId } = mapdata;
      const { vgID: currentFloorVGID } = floorMapping[currentFloorId];
      const { vgID: imdFloorVGID } = floorMapping[imdFloorId];

      if (!activeServiceType) return console.error('Missing activeServiceType', activeServiceType);
      if (!serviceType) return console.error('Missing serviceType', serviceId, serviceMapping);

      if(activeServiceType.id == serviceType.id) {
        // If same service is selected, clear everything and reset to imd floor
        Map.setFloor(imdFloorId);
        middleware.changeFloor(imdFloorId);
        return;
      } else {
        mapdata.activeServiceType = serviceType;
      }

      return getClosestService(serviceId, vgContext)
        .then(({ floor }) => {

          const serviceOnCurrentFloor = floor == currentFloorVGID;
          const serviceOnIMDFloor = floor == imdFloorVGID;

          // IMD IS LOCATED ON CURRENT FLOOR
          if (imdFloorId == currentFloorId) {
            // One or more selected servicetypes can be found on current floor
            if (serviceOnCurrentFloor) {
              middleware.popService(serviceId);
              Map.showServiceMenu(serviceId, true);
            } else {
              // Service can not be found on this floor -> show popup
              Map.showModalServiceMenu(serviceId);
            }
          }
          // IMD IS NOT LOCATED ON CURRENT FLOOR
          else {
            // Service is available in both floors or service is available in current floor
            if (serviceOnCurrentFloor && serviceOnIMDFloor || serviceOnCurrentFloor) {
              middleware.popService(serviceId);
              Map.showServiceMenu(serviceId, true);
            }
            // Service is available SAME floor with IMD or service is available in SOME OTHER third floor
            else {
              //Service can not be found on this floor -> show popup
              Map.showModalServiceMenu(serviceId);
            }
          }
        })
        .catch(e => {
          console.error(e);
          // TODO improve here. Use custom error to pass failed ID, filter it out,
          // and run search again.
        });
    }).catch(logError);
  }

  return {
    start,
    exit,
    refresh
  };
}

